import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import moment from 'moment';

import { showSuccessSnackbar } from 'modules/snackbar';
import { addAllList } from 'modules/common';
import { getDate } from 'lib/commonLib';
import {
  SoldOutList,
  SoldOutWrite,
  SoldOutKeyEdit,
  SoldOutEdit,
  SoldOutEditView,
} from 'lib/api/soldout';

import NewButton from 'components/molecules/NewButton';
import NewTableContainer from 'components/molecules/NewTable';
import NewFilterWrap from 'components/molecules/NewFilterWrap';
import NewCommonFilter from 'components/organisms/NewCommonFilter';
import FilterCollection from 'components/organisms/FilterCollection';

import StatisticsModal from './StatisticsModal';

const Statistics = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const initParams = {
    sDate: getDate(moment(moment().subtract(13, 'month').format('YYYY-MM-DD'))),
    eDate: moment().format('YYYYMMDD'),
    trxYn: 'all',
    batKnd: 'all',
    sort: 'asc',
    order: 'ADVER_ID',
  };
  const [initialization, setInitialization] = useState(false);
  const [params, setParams] = useState(initParams);

  const list = {
    trxYn: [
      { label: '사용', value: '01' },
      { label: '미사용', value: '02' },
    ],
    batKnd: [
      { label: 'CRAWLING', value: '01' },
      { label: 'CAFE24', value: '02' },
    ],
    mode: [
      { label: '품절 사용 여부', value: '01' },
      { label: '품절 구분', value: '02' },
    ],
  };

  const initModal = {
    open: false,
    code: null,
    params: { apiAuth: 'mall.read_product' },
    message: '',
  };
  const [modal, setModal] = useState(initModal);
  const [reFetch, setReFetch] = useState(false);

  useEffect(() => {
    if (initialization === true) {
      setInitialization(false);
    }
  }, [initialization]);

  const handleModalOpen = (type, rowObj) => {
    let newModal = { ...initModal };
    const { name } = type.currentTarget;
    newModal = { ...newModal, code: name, open: true };
    if (name === 'add') {
      newModal = {
        ...newModal,
        params: { ...newModal.params, adverId: rowObj['ADVER_ID'] },
        modalTitle: 'CAFE24 KEY 등록',
        confirmBtnTitle: '등록',
      };
      setModal(newModal);
    } else if (name === 'edit') {
      newModal = {
        ...newModal,
        modalTitle: 'CAFE24 KEY 수정',
        confirmBtnTitle: '수정',
      };
      SoldOutEditView({ adverId: rowObj['ADVER_ID'] })
        .then((response) => {
          const { CAFE24_ID, CAFE24_PWD, APP_ID, APP_PWD, API_AUTH } =
            response.data.data;
          newModal = {
            ...newModal,
            params: {
              ...newModal.params,
              adverId: rowObj['ADVER_ID'],
              cafeId: CAFE24_ID,
              cafePw: CAFE24_PWD,
              appId: APP_ID,
              appPw: APP_PWD,
              apiAuth: API_AUTH,
            },
          };
        })
        .catch((error) => {
          handleClickSnackbar({ message: error, option: { type: 'error' } });
          newModal = { ...initModal };
        })
        .finally(() => {
          setModal(newModal);
        });
    } else if (name === 'soldOutEdit') {
      newModal = {
        ...newModal,
        params: {
          adverId: rowObj['ADVER_ID'],
          batKnd: rowObj['BAT_KND'],
          trxYn: rowObj['TRX_YN'],
        },
        modalTitle: '수정',
        confirmBtnTitle: '수정',
      };
      setModal(newModal);
    }
  };
  /**
   * 모달에 submit 버튼을 눌렀을 때 실행되는 함수
   */
  const handleSubmit = () => {
    let newParams = { ...modal.params };
    for (const [key, value] of Object.entries(newParams)) {
      if (value === null) {
        delete newParams[key];
      }
    }

    if (modal.code === 'add') {
      SoldOutWrite(newParams)
        .then((response) => {
          const { message } = response.data;
          handleClickSnackbar({
            message: message,
            option: { type: 'success' },
          });
          setReFetch(true);
        })
        .catch((error) => {
          handleClickSnackbar({ message: error, option: { type: 'error' } });
        })
        .finally(() => {
          setReFetch(false);
          setModal(initModal);
        });
    } else if (modal.code === 'edit') {
      SoldOutKeyEdit(newParams)
        .then((response) => {
          const { message } = response.data;
          handleClickSnackbar({
            message: message,
            option: { type: 'success' },
          });
          setReFetch(true);
        })
        .catch((error) => {
          handleClickSnackbar({ message: error, option: { type: 'error' } });
        })
        .finally(() => {
          setReFetch(false);
          setModal(initModal);
        });
    } else if (modal.code === 'soldOutEdit') {
      SoldOutEdit(newParams)
        .then((response) => {
          const { message } = response.data;
          setModal({ ...initModal, params: initModal.params });
          handleClickSnackbar({
            message: message,
            option: { type: 'success' },
          });
          setReFetch(true);
        })
        .catch((error) => {
          setModal(initModal);
          handleClickSnackbar({ message: error, option: { type: 'error' } });
        })
        .finally(() => {
          setReFetch(false);
        });
    }
  };

  const handleOnReset = () => {
    if (JSON.stringify(params) !== JSON.stringify(initParams)) {
      setInitialization(true);
      setParams(initParams);
    }
  };

  const getParam = (newParams) => {
    if (JSON.stringify(params) !== JSON.stringify(newParams)) {
      setParams(newParams);
    }
  };

  const modalGetParam = (newParams) => {
    if (JSON.stringify(modal['params']) !== JSON.stringify(newParams)) {
      setModal({ ...modal, params: newParams });
    }
  };

  const handleModalClose = () => {
    setModal({ ...modal, open: false });
  };

  const addColumnList = (() => {
    const result = new Map();
    result.set(0, {
      id: 'MANAGE_ICONS',
      label: t('관리'),
      align: 'center',
      makeRowFunc: (rowObj) => {
        return (
          <NewButton
            name="soldOutEdit"
            title={t('품절 사용 여부 or 품절 구분 수정')}
            iconBtn
            icon="faEdit"
            onClick={(e) => handleModalOpen(e, rowObj)}
            disableRipple
          />
        );
      },
    });
    return result;
  })();

  const customized = {
    TRX_YN: {
      makeBody: (rowObj, headsMap) => {
        return rowObj['TRX_YN'] === '01'
          ? '사용 '
          : rowObj['TRX_YN'] === '02'
          ? '미사용'
          : '데이터 오류';
      },
    },
    BAT_KND: {
      makeBody: (rowObj, headsMap) => {
        return rowObj['BAT_KND'] === '01'
          ? 'CRAWLING '
          : rowObj['BAT_KND'] === '02'
          ? 'CAFE24'
          : '데이터 오류';
      },
    },
    REG_YN: {
      makeHead: (headsMap) => {
        return 'CAFE24 KEY 등록/수정';
      },
      makeBody: (rowObj, headsMap) => {
        return rowObj['REG_YN'] === '01' ? (
          <NewButton
            title={t('CAFE24 KEY 수정')}
            iconBtn
            icon="faEdit"
            name="edit"
            onClick={(e) => handleModalOpen(e, rowObj)}
            disableRipple
          />
        ) : (
          <NewButton
            title={t('CAFE24 KEY 등록')}
            iconBtn
            icon="faPlus"
            name="add"
            onClick={(e) => handleModalOpen(e, rowObj)}
            disableRipple
          />
        );
      },
    },
  };

  const handleClickSnackbar = useCallback((snackbarOptions) => {
    dispatch(showSuccessSnackbar(snackbarOptions));
    //eslint-disable-next-line
  }, []);

  return (
    <>
      <NewFilterWrap handleOnReset={handleOnReset}>
        <NewCommonFilter
          initialization={initialization}
          getParam={getParam}
          params={params}
          usePltfomTpCodeFilter={false}
          useDisplayFilter={false}
          useExternalFilter={false}
        />
        <FilterCollection
          label={t('품절사용 여부')}
          name="trxYn"
          params={params}
          value={params.trxYn}
          type="radio"
          list={addAllList(list.trxYn, 'label', 'value', 'all')}
          callBack={getParam}
          labelKey="label"
          valueKey="value"
        />
        <FilterCollection
          label={t('품절구분')}
          name="batKnd"
          params={params}
          value={params.batKnd}
          type="radio"
          list={addAllList(list.batKnd, 'label', 'value', 'all')}
          callBack={getParam}
          labelKey="label"
          valueKey="value"
        />
      </NewFilterWrap>

      <NewTableContainer
        dataReqPromise={SoldOutList}
        params={params}
        excelTotalRemoval
        customized={customized}
        addColumnList={addColumnList}
        reFetch={reFetch}
        useTotal={false}
        getParam={getParam}
        toolTip={t(
          '카페24 API를 사용 중인 광고주의 품절 관련 관리를 위한 페이지'
        )}
        excelItemsRemoval="regYn"
      />

      <StatisticsModal
        handleClickSnackbar={handleClickSnackbar}
        modal={modal}
        handleModalClose={handleModalClose}
        getParam={modalGetParam}
        handleSubmit={handleSubmit}
        list={list}
      />
    </>
  );
};

export default Statistics;
